import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Map, Marker, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import useTranslate from '../hooks/useTranslate.jsx';

const GRAYSCALE_STYLES = [
  { elementType: 'geometry', stylers: [{ saturation: -100 }, { lightness: 5 }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#6b6b6b' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#cfcfcf' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#e8e8e8' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d6d6d6' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9a9a9a' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f2f2f2' }] },
];

const PIN_SVG = `
<?xml version="1.0" encoding="UTF-8"?>
<svg id="Livello_1" data-name="Livello 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 155.99 259.11">
  <defs>
    <style>
      .cls-1 {
        fill: #1d1d1b;
      }

      .cls-2 {
        fill: #fff;
      }
    </style>
  </defs>
  <g id="nA2gSH">
    <path class="cls-1" d="M77.84,0c21.32-.28,44.91,7.58,62.53,31.03,10.84,14.43,16.05,30.71,15.6,48.73-.2,8.15-2.22,16.01-4.89,23.65-4.05,11.59-8.03,23.22-12.71,34.56-5.26,12.75-11.11,25.27-17,37.75-5.39,11.42-11.04,22.72-16.83,33.94-6.49,12.57-13.24,25-19.92,37.47-2.1,3.93-4.31,7.8-6.62,11.96-2.31-4.16-4.52-8.03-6.62-11.96-6.67-12.47-13.43-24.9-19.92-37.47-5.79-11.22-11.44-22.52-16.83-33.94-5.89-12.48-11.74-25-17-37.75-4.68-11.34-8.66-22.98-12.71-34.56C2.25,95.79.23,87.92.03,79.77c-.45-18.03,4.76-34.3,15.6-48.73C33.24,7.59,56.52-.27,77.84,0h0Z"/>
  </g>
  <path class="cls-2" d="M57.03,86.86h-30.31v-4.51h30.31v-16.7c0-9.85,6.97-13.95,15.45-13.95,2.05,0,6.03.6,8.08,1.01v4.51c-2.05-.41-6.03-1.01-8.08-1.01-5.74,0-10.25,2.05-10.25,9.84v16.29h27.71v-16.7c0-9.85,6.97-13.95,15.45-13.95,2.05,0,6.03.6,8.08,1.01v4.51c-2.05-.41-6.03-1.01-8.08-1.01-5.74,0-10.25,2.05-10.25,9.84v16.29h30.31v4.51h-30.31v63.56h-5.2v-63.56h-27.71v63.56h-5.2v-63.56Z"/>
</svg>
`.trim();

const PIN_SVG_ALT = `
<?xml version="1.0" encoding="UTF-8"?>
<svg id="Livello_1" data-name="Livello 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 159.4 265.33">
  <defs>
    <style>
      .cls-1 {
        fill: #fff;
        stroke: #000;
        stroke-miterlimit: 10;
        stroke-width: 3px;
      }
    </style>
  </defs>
  <g id="nA2gSH">
    <path class="cls-1" d="M79.49,2.1c21.32-.28,44.91,7.58,62.53,31.03,10.84,14.43,16.05,30.71,15.6,48.73-.2,8.15-2.22,16.01-4.89,23.65-4.05,11.59-8.03,23.22-12.71,34.56-5.26,12.75-11.11,25.27-17,37.75-5.39,11.42-11.04,22.72-16.83,33.94-6.49,12.57-13.24,25-19.92,37.47-2.1,3.93-4.31,7.8-6.62,11.96-2.31-4.16-4.52-8.03-6.62-11.96-6.67-12.47-13.43-24.9-19.92-37.47-5.79-11.22-11.44-22.52-16.83-33.94-5.89-12.48-11.74-25-17-37.75-4.68-11.34-8.66-22.98-12.71-34.56-2.67-7.64-4.69-15.5-4.89-23.65-.45-18.03,4.76-34.3,15.6-48.73C34.89,9.68,58.18,1.82,79.49,2.1h0Z"/>
  </g>
  <path d="M58.68,88.95h-30.31v-4.51h30.31v-16.7c0-9.85,6.97-13.95,15.45-13.95,2.05,0,6.03.6,8.08,1.01v4.51c-2.05-.41-6.03-1.01-8.08-1.01-5.74,0-10.25,2.05-10.25,9.84v16.29h27.71v-16.7c0-9.85,6.97-13.95,15.45-13.95,2.05,0,6.03.6,8.08,1.01v4.51c-2.05-.41-6.03-1.01-8.08-1.01-5.74,0-10.25,2.05-10.25,9.84v16.29h30.31v4.51h-30.31v63.56h-5.2v-63.56h-27.71v63.56h-5.2v-63.56Z"/>
</svg>
`.trim();

const PIN_URL = `data:image/svg+xml;utf-8,${encodeURIComponent(PIN_SVG)}`;
const PIN_URL_ALT = `data:image/svg+xml;utf-8,${encodeURIComponent(PIN_SVG_ALT)}`;

// sotto questo zoom i punti vengono raggruppati in cluster
const CLUSTER_MAX_ZOOM = 11;
// raggio di aggregazione, in pixel schermo
const CLUSTER_RADIUS_PX = 60;
// zoom massimo dopo la ricerca di un indirizzo
const SEARCH_MAX_ZOOM = 16;

function clusterIcon(count) {
  const size = count < 10 ? 36 : count < 100 ? 44 : 52;
  const r = size / 2;
  const fontSize = count < 100 ? 14 : 13;
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
  <circle cx="${r}" cy="${r}" r="${r}" fill="#000"/>
  <text x="${r}" y="${r}" font-family="HelveticaNeue, sans-serif" font-size="${fontSize}" font-weight="600" font-variant-numeric="lining-nums" fill="#fff" text-anchor="middle" dominant-baseline="central">${count}</text>
</svg>`.trim();
  const url = `data:image/svg+xml;utf-8,${encodeURIComponent(svg)}`;
  return url;
  if (typeof window === 'undefined' || !window.google) return url;
  return {
    url,
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(r, r),
  };
}

// web mercator: lat/lng -> pixel alla scala dello zoom corrente
function project(lat, lng, zoom) {
  const scale = 256 * Math.pow(2, zoom);
  const siny = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
  return {
    x: scale * (0.5 + lng / 360),
    y: scale * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)),
  };
}

// aggregazione per distanza: ogni punto entra nel gruppo piu' vicino
// entro CLUSTER_RADIUS_PX, altrimenti ne apre uno nuovo
function buildClusters(stores, zoom) {
  const points = stores
    .map((s) => ({ store: s, ...project(s.lat, s.lng, zoom) }))
    // ordine stabile: stessi cluster a parita' di input
    .sort((a, b) => a.x - b.x || a.y - b.y);

  const groups = [];
  points.forEach((p) => {
    let target = null;
    let bestDist = Infinity;
    groups.forEach((g) => {
      const d = Math.hypot(p.x - g.x, p.y - g.y);
      if (d <= CLUSTER_RADIUS_PX && d < bestDist) {
        bestDist = d;
        target = g;
      }
    });
    if (!target) {
      groups.push({ items: [p], x: p.x, y: p.y });
      return;
    }
    target.items.push(p);
    target.x = target.items.reduce((a, i) => a + i.x, 0) / target.items.length;
    target.y = target.items.reduce((a, i) => a + i.y, 0) / target.items.length;
  });

  const clusters = [];
  const singles = [];
  groups.forEach((g) => {
    const group = g.items.map((i) => i.store);
    if (group.length === 1) {
      singles.push(group[0]);
      return;
    }
    const lat = group.reduce((a, s) => a + s.lat, 0) / group.length;
    const lng = group.reduce((a, s) => a + s.lng, 0) / group.length;
    clusters.push({
      key: group.map((s) => s.id).join('-'),
      lat,
      lng,
      stores: group,
    });
  });

  return { clusters, singles };
}

function BoundsFitter({ stores, selectedStore, userLocation, searchLocation }) {
  const map = useMap();
  const didInitialFit = useRef(false);

  useEffect(() => {
    if (!map || stores.length === 0) return;
    if (didInitialFit.current) return;
    const bounds = new window.google.maps.LatLngBounds();
    stores.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
    map.fitBounds(bounds, 60);
    didInitialFit.current = true;
  }, [map, stores]);

  useEffect(() => {
    if (!map || !selectedStore) return;
    map.panTo({ lat: selectedStore.lat, lng: selectedStore.lng });
    const current = map.getZoom() ?? 6;
    if (current < 13) map.setZoom(13);
  }, [map, selectedStore]);

  useEffect(() => {
    if (!map || !userLocation) return;
    map.panTo({ lat: userLocation.lat, lng: userLocation.lng });
    const current = map.getZoom() ?? 6;
    if (current < 13) map.setZoom(13);
  }, [map, userLocation]);

  useEffect(() => {
    if (!map || !searchLocation) return;

    // il viewport del place dà lo zoom giusto per nazione/regione/città/civico
    if (searchLocation.viewport) {
      map.fitBounds(searchLocation.viewport, 40);
      // sui civici il viewport è di pochi metri: non si scende sotto la via
      const clamp = map.addListener('idle', () => {
        clamp.remove();
        if ((map.getZoom() ?? 0) > SEARCH_MAX_ZOOM) map.setZoom(SEARCH_MAX_ZOOM);
      });
      return () => clamp.remove();
    }

    map.panTo({ lat: searchLocation.lat, lng: searchLocation.lng });
    const current = map.getZoom() ?? 6;
    if (current < 13) map.setZoom(13);
  }, [map, searchLocation]);

  return null;
}

// riporta i bounds visibili a ogni idle (pan, zoom, resize)
function ViewportReporter({ onBoundsChange }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !onBoundsChange) return;
    const emit = () => {
      const b = map.getBounds();
      if (b) onBoundsChange(b.toJSON());
    };
    emit();
    const listener = map.addListener('idle', emit);
    return () => listener.remove();
  }, [map, onBoundsChange]);

  return null;
}

function MarkerLayer({ stores, iconConfig, onMarkerClick }) {
  const map = useMap();
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    if (!map) return;
    const sync = () => setZoom(map.getZoom() ?? null);
    sync();
    const listener = map.addListener('zoom_changed', sync);
    return () => listener.remove();
  }, [map]);

  const { clusters, singles } = useMemo(() => {
    if (zoom === null || zoom > CLUSTER_MAX_ZOOM) {
      return { clusters: [], singles: stores };
    }
    return buildClusters(stores, zoom);
  }, [stores, zoom]);

  const zoomToCluster = (cluster) => {
    if (!map) return;
    const bounds = new window.google.maps.LatLngBounds();
    cluster.stores.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
    map.fitBounds(bounds, 80);
    // punti sovrapposti: fitBounds non cambia zoom, forzo un passo in avanti
    const current = map.getZoom() ?? 0;
    if (current <= CLUSTER_MAX_ZOOM) {
      map.setZoom(Math.min(current + 2, CLUSTER_MAX_ZOOM + 2));
      map.panTo({ lat: cluster.lat, lng: cluster.lng });
    }
  };

  return (
    <>
      {clusters.map((cluster) => (
        <Marker
          key={`cluster-${cluster.key}`}
          position={{ lat: cluster.lat, lng: cluster.lng }}
          icon={clusterIcon(cluster.stores.length)}
          zIndex={1000}
          onClick={() => zoomToCluster(cluster)}
        />
      ))}
      {singles.map((store) => (
        <Marker
          key={store.id}
          position={{ lat: store.lat, lng: store.lng }}
          icon={iconConfig(store)}
          onClick={() => onMarkerClick(store)}
        />
      ))}
    </>
  );
}

export default function MapView({
  stores,
  selectedStore,
  onSelect,
  categories,
  userLocation,
  searchLocation,
  onBoundsChange,
}) {
  const [infoStoreId, setInfoStoreId] = useState(null);
  const { t } = useTranslate();

  const categoryMap = useMemo(() => {
    const m = {};
    categories.forEach((c) => {
      m[c.id] = c;
    });
    return m;
  }, [categories]);

  useEffect(() => {
    if (selectedStore) setInfoStoreId(selectedStore.id);
  }, [selectedStore]);

  const infoStore = stores.find((s) => s.id === infoStoreId) || null;

  const iconConfig = useCallback(
    (store) => {
      const url = store?.shop_types?.includes('boutique') ? PIN_URL : PIN_URL_ALT;

      if (typeof window === 'undefined' || !window.google) return url;

      return {
        url,
        scaledSize: { width: 34, height: 44 },
        anchor: { x: 17, y: 44 },
      };
    },
    [stores],
  );

  return (
    <div className="map-wrap">
      <Map
        defaultCenter={{ lat: 42.5, lng: 12.5 }}
        defaultZoom={6}
        minZoom={2}
        gestureHandling="greedy"
        disableDefaultUI={false}
        mapTypeControl
        streetViewControl={false}
        fullscreenControl={false}
        styles={GRAYSCALE_STYLES}
      >
        <BoundsFitter
          stores={stores}
          selectedStore={selectedStore}
          userLocation={userLocation}
          searchLocation={searchLocation}
        />
        <ViewportReporter onBoundsChange={onBoundsChange} />
        {userLocation && (
          <Marker position={{ lat: userLocation.lat, lng: userLocation.lng }} title={t('map.yourLocation')} />
        )}
        <MarkerLayer
          stores={stores}
          iconConfig={iconConfig}
          onMarkerClick={(store) => {
            onSelect(store.id);
            setInfoStoreId(store.id);
          }}
        />
        {infoStore && (
          <InfoWindow
            position={{ lat: infoStore.lat, lng: infoStore.lng }}
            pixelOffset={[0, -44]}
            onCloseClick={() => setInfoStoreId(null)}
          >
            <div className="info-window">
              <span className="info-cat">{infoStore.shop_types[0]}</span>
              <h2>{infoStore.store_name.toLowerCase()}</h2>
              <p className="info-address">
                {infoStore.address.map((line, i) => (
                  <span key={i}>
                    {i > 0 && <br />}
                    {line.toLowerCase()}
                  </span>
                ))}
              </p>
            </div>
          </InfoWindow>
        )}
      </Map>
    </div>
  );
}
